import { useParams, Navigate, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { SEOHead } from '../components/SEOHead';
import { TourFigure, TOUR_PHOTOS_2026 } from '../components/TourFigure';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { DESTINATIONS_DATA } from '../data/destinationsData';
import { translations } from '../translations';

export default function DestinationPage({ language, setLanguage }) {
  const { slug } = useParams();
  const t = translations[language];
  const dest = DESTINATIONS_DATA.find(d => d.slug === slug);
  const isAr = language === 'ar';

  if (!dest) return <Navigate to="/" replace />;

  const title = isAr ? (dest.titleAr || dest.title) : dest.title;
  const content = isAr ? (dest.contentAr || dest.content) : dest.content;

  const schema = {
    "@context": "https://schema.org",
    "@type": "TouristDestination",
    "name": dest.title,
    "description": dest.metaDescription,
    "url": `https://viaturkey.net/istanbul/${dest.slug}`,
    "touristType": "Gulf Arab travelers"
  };

  return (
    <div className="destination-page" dir={isAr ? 'rtl' : 'ltr'} data-testid="destination-page">
      <SEOHead
        title={isAr ? (dest.metaTitleAr || dest.metaTitle) : dest.metaTitle}
        description={isAr ? (dest.metaDescriptionAr || dest.metaDescription) : dest.metaDescription}
        canonical={`https://viaturkey.net/istanbul/${dest.slug}`}
        schema={schema}
      />
      <Header language={language} t={t} />

      <section className="sp-hero" data-testid="dest-hero" style={{backgroundImage: `url(${dest.heroImage})`}}>
        <div className="sp-hero-overlay"></div>
        <div className="sp-hero-content">
          <h1>{title}</h1>
        </div>
      </section>

      <div className="sp-container">
        <nav className="dest-breadcrumb" data-testid="dest-breadcrumb">
          <Link to="/">{isAr ? 'الرئيسية' : 'Home'}</Link> / <Link to="/istanbul/galataport">{isAr ? 'إسطنبول' : 'Istanbul'}</Link> / <span>{title}</span>
        </nav>

        <div className="sp-body dest-content" data-testid="dest-body" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }} />

        {dest.photoKey && <TourFigure photo={TOUR_PHOTOS_2026[dest.photoKey]} testId={`dest-photo-${dest.slug}`} />}

        {dest.relatedService && (
          <div className="dest-service-link" data-testid="dest-service-link">
            <Link to={`/tours/${dest.relatedService.slug}`} className="sp-cta-btn">
              <i className="fas fa-arrow-right"></i> {isAr ? (dest.relatedService.labelAr || dest.relatedService.label) : dest.relatedService.label}
            </Link>
          </div>
        )}

        <div className="sp-cta" data-testid="dest-cta">
          <h2>{isAr ? `استكشف ${title.replace(' إسطنبول', '')} مع Viaturkey` : `Explore ${dest.title.replace(' Istanbul', '')} with Viaturkey`}</h2>
          <p>{isAr ? 'احجز جولة خاصة مع مرشد يتحدث العربية. برنامج مخصص، سيارة خاصة، طعام حلال — إسطنبولك بطريقتك.' : 'Book a private tour with an Arabic-speaking guide. Personalized itinerary, private vehicle, halal dining — your Istanbul, your way.'}</p>
          <a href={`https://wa.me/905075337545?text=${encodeURIComponent(`Hi Viaturkey! I'd like to explore ${dest.title}.`)}`} target="_blank" rel="noopener noreferrer" className="sp-cta-btn" data-testid="dest-whatsapp-btn">
            <i className="fab fa-whatsapp"></i> {isAr ? 'راسلنا على واتساب' : 'Message Us on WhatsApp'}
          </a>
        </div>

        <div className="dest-other-areas">
          <h2>{isAr ? 'استكشف المزيد من مناطق إسطنبول' : 'Explore More Istanbul Areas'}</h2>
          <div className="sp-related-links">
            {DESTINATIONS_DATA.filter(d => d.slug !== slug).map(d => (
              <Link key={d.slug} to={`/istanbul/${d.slug}`} className="sp-related-link" data-testid={`area-${d.slug}`}>
                <i className="fas fa-map-marker-alt"></i> {isAr ? (d.titleAr || d.title) : d.title}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <Footer t={t} />
    </div>
  );
}
